/**
 * USPTO Holiday Schedule and Deadline Calculator
 * Calculates response deadlines for office actions considering weekends and holidays
 */

// USPTO Holidays for 2025-2027
const USPTO_HOLIDAYS: { [year: number]: Date[] } = {
  2025: [
    new Date(2025, 0, 1),   // New Year's Day (Wednesday, January 01)
    new Date(2025, 0, 20),  // Birthday of Martin Luther King, Jr. (Monday, January 20)
    new Date(2025, 0, 20),  // Inauguration Day (Monday, January 20)
    new Date(2025, 1, 17),  // Washington's Birthday (Monday, February 17)
    new Date(2025, 4, 26),  // Memorial Day (Monday, May 26)
    new Date(2025, 5, 19),  // Juneteenth National Independence Day (Thursday, June 19)
    new Date(2025, 6, 4),   // Independence Day (Friday, July 04)
    new Date(2025, 8, 1),   // Labor Day (Monday, September 01)
    new Date(2025, 9, 13),  // Columbus Day (Monday, October 13)
    new Date(2025, 10, 11), // Veterans Day (Tuesday, November 11)
    new Date(2025, 10, 27), // Thanksgiving Day (Thursday, November 27)
    new Date(2025, 11, 25), // Christmas Day (Thursday, December 25)
  ],
  2026: [
    new Date(2026, 0, 1),   // New Year's Day (Thursday, January 01)
    new Date(2026, 0, 19),  // Birthday of Martin Luther King, Jr. (Monday, January 19)
    new Date(2026, 1, 16),  // Washington's Birthday (Monday, February 16)
    new Date(2026, 4, 25),  // Memorial Day (Monday, May 25)
    new Date(2026, 5, 19),  // Juneteenth National Independence Day (Friday, June 19)
    new Date(2026, 6, 3),   // Independence Day (Friday, July 03)
    new Date(2026, 8, 7),   // Labor Day (Monday, September 07)
    new Date(2026, 9, 12),  // Columbus Day (Monday, October 12)
    new Date(2026, 10, 11), // Veterans Day (Wednesday, November 11)
    new Date(2026, 10, 26), // Thanksgiving Day (Thursday, November 26)
    new Date(2026, 11, 25), // Christmas Day (Friday, December 25)
  ],
  2027: [
    new Date(2027, 0, 1),   // New Year's Day
    new Date(2027, 0, 18),  // Birthday of Martin Luther King, Jr.
    new Date(2027, 1, 15),  // Washington's Birthday
    new Date(2027, 4, 31),  // Memorial Day
    new Date(2027, 5, 18),  // Juneteenth (observed on Friday)
    new Date(2027, 6, 5),   // Independence Day (observed on Monday)
    new Date(2027, 8, 6),   // Labor Day
    new Date(2027, 9, 11),  // Columbus Day
    new Date(2027, 10, 11), // Veterans Day
    new Date(2027, 10, 25), // Thanksgiving Day
    new Date(2027, 11, 24), // Christmas Day (observed)
  ],
};

/**
 * Check if a date is a weekend (Saturday or Sunday)
 */
function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6; // Sunday = 0, Saturday = 6
}

/**
 * Check if a date is a USPTO holiday
 */
function isHoliday(date: Date): boolean {
  const year = date.getFullYear();
  const holidays = USPTO_HOLIDAYS[year] || [];

  return holidays.some(holiday =>
    holiday.getFullYear() === date.getFullYear() &&
    holiday.getMonth() === date.getMonth() &&
    holiday.getDate() === date.getDate()
  );
}

/**
 * Check if a date is a business day (not weekend and not holiday)
 */
function isBusinessDay(date: Date): boolean {
  return !isWeekend(date) && !isHoliday(date);
}

/**
 * Move a date to the next business day if it falls on a weekend or holiday
 */
function moveToNextBusinessDay(date: Date): Date {
  const result = new Date(date);

  while (!isBusinessDay(result)) {
    result.setDate(result.getDate() + 1);
  }

  return result;
}

/**
 * Add months to a date
 */
function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

/**
 * Calculate deadline for office action response
 * @param startDate - The date of the office action (mail date)
 * @param months - Number of months for the deadline (typically 3 or 6)
 * @returns The deadline date, adjusted to next business day if needed
 */
export function calculateDeadline(startDate: Date, months: number): Date {
  const targetDate = addMonths(startDate, months);
  return moveToNextBusinessDay(targetDate);
}

/**
 * Calculate both 3-month and 6-month deadlines for an office action
 * @param officeActionDate - The date of the office action (mail date)
 * @returns Object with 3-month and 6-month deadlines
 */
export function calculateOADeadlines(officeActionDate: Date): {
  threeMonthDeadline: Date;
  sixMonthDeadline: Date;
  threeMonthDeadlineFormatted: string;
  sixMonthDeadlineFormatted: string;
} {
  const threeMonthDeadline = calculateDeadline(officeActionDate, 3);
  const sixMonthDeadline = calculateDeadline(officeActionDate, 6);

  const formatOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };

  return {
    threeMonthDeadline,
    sixMonthDeadline,
    threeMonthDeadlineFormatted: threeMonthDeadline.toLocaleDateString('en-US', formatOptions),
    sixMonthDeadlineFormatted: sixMonthDeadline.toLocaleDateString('en-US', formatOptions),
  };
}

/**
 * Calculate days remaining until a deadline
 * @param deadline - The deadline date
 * @returns Number of days remaining (negative if past due)
 */
export function daysUntilDeadline(deadline: Date): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  deadline.setHours(0, 0, 0, 0);

  const diffTime = deadline.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}
